/* eslint-disable */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Crown,
  Handshake,
  Users as UsersIcon,
  KeyRound,
  Phone,
  MessageCircle,
  ImagePlus,
  Trash2,
  Tag,
  Home as HomeIcon,
} from "lucide-react";
import { apiFetch, getApiUrl } from "../auth/apiClient";
import "./VillaPage.css";

const ROLE_META = {
  PRIMARY_OWNER: { Icon: Crown, label: "Primary Owner", chip: "owner-primary" },
  SHARED_OWNER: { Icon: Handshake, label: "Shared Owner", chip: "owner-shared" },
  RESIDENT: { Icon: UsersIcon, label: "Resident", chip: "resident-family" },
  TENANT: { Icon: KeyRound, label: "Tenant", chip: "resident-tenant" },
};

function MemberCard({ m }) {
  const meta = ROLE_META[m.role] || {};
  const Icon = meta.Icon;
  return (
    <article className="vsv-member">
      <div className="vsv-member-head">
        <Link to={`/profile/${m.user.id}`} className="vsv-member-name">
          {m.user.name || "(unnamed)"}
        </Link>
        <span className={`vsv-chip vsv-chip-${meta.chip || "neutral"}`}>
          {Icon && <Icon size={11} strokeWidth={2.2} />} {meta.label || m.role}
        </span>
      </div>
      <div className="vsv-member-meta">
        {m.phone_visible ? (
          <a href={`tel:+91${m.user.phone_number}`} className="vsv-phone-link">
            <Phone size={12} /> +91 {m.user.phone_number}
          </a>
        ) : (
          <span className="vsv-phone-masked">
            <Phone size={12} /> +91 {m.user.phone_number}
          </span>
        )}
        {m.phone_visible && (
          <a
            href={`https://wa.me/91${m.user.phone_number}`}
            target="_blank"
            rel="noreferrer"
            className="vsv-wa-link"
            aria-label="WhatsApp"
            title="WhatsApp"
          >
            <MessageCircle size={14} />
          </a>
        )}
        {m.relation && <span className="vsv-relation">· {m.relation}</span>}
        {m.tenant_end_date && (
          <span className="vsv-lease">· lease ends {m.tenant_end_date}</span>
        )}
      </div>
    </article>
  );
}

function PhotoGrid({ photos, canEdit, villaNumber, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await apiFetch(
        `/api/villas/by-number/${encodeURIComponent(villaNumber)}/photos`,
        { method: "POST", formData: fd, authRequired: true }
      );
      onChanged && onChanged();
    } catch (err) {
      setError(err?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await apiFetch(
        `/api/villas/by-number/${encodeURIComponent(villaNumber)}/photos/${photoId}`,
        { method: "DELETE", authRequired: true }
      );
      onChanged && onChanged();
    } catch (err) {
      alert(err?.message || "Delete failed");
    }
  };

  return (
    <section className="vsv-section">
      <header className="vsv-section-head">
        <h2>Villa photos</h2>
        {canEdit && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={upload}
              style={{ display: "none" }}
            />
            <button
              className="vsv-btn-primary"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
            >
              <ImagePlus size={14} />
              {uploading ? "Uploading…" : "Add photo"}
            </button>
          </>
        )}
      </header>

      {error && <div className="vsv-error">{error}</div>}

      {photos.length === 0 ? (
        <div className="vsv-empty">
          {canEdit
            ? "No photos yet. Upload a few — they'll auto-attach to any rent/sale listing you create from this villa."
            : "No photos uploaded for this villa yet."}
        </div>
      ) : (
        <div className="vsv-grid">
          {photos.map((p) => (
            <figure key={p.id} className="vsv-photo">
              <img src={`${getApiUrl()}${p.url}`} alt={p.caption || "Villa photo"} />
              {p.caption && <figcaption>{p.caption}</figcaption>}
              {canEdit && (
                <button
                  className="vsv-photo-del"
                  onClick={() => remove(p.id)}
                  aria-label="Delete photo"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </figure>
          ))}
        </div>
      )}
    </section>
  );
}

function fmtRequestedDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

// Pending membership approvals for THIS villa. `/api/approvals/pending`
// returns all rows for admins and the caller's-villa resident/tenant rows for
// approved primary owners (403 for everyone else — we just hide the section).
function PendingApprovalsSection({ villaNumber, onDecided }) {
  const [items, setItems] = useState(null); // null = not loaded / not allowed
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const rows = await apiFetch("/api/approvals/pending", {
        authRequired: true,
      });
      setItems(
        (rows || []).filter((r) => r.villa?.villa_number === villaNumber)
      );
    } catch (_) {
      // Not an admin/owner (403) or transient failure — no section.
      setItems(null);
    }
  }, [villaNumber]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (row, action) => {
    let reason = null;
    if (action === "reject") {
      reason = window.prompt(
        `Reject ${row.user?.name || "this request"}? Add a reason (optional):`
      );
      if (reason === null) return; // cancelled
    }
    setBusyId(row.id);
    setError("");
    try {
      await apiFetch(`/api/approvals/${row.id}/${action}`, {
        method: "POST",
        authRequired: true,
        body: action === "reject" ? { reason: reason.trim() || null } : {},
      });
      await load();
      onDecided && onDecided();
    } catch (e) {
      setError(e?.message || "Could not update the request");
    } finally {
      setBusyId("");
    }
  };

  if (!items || items.length === 0) return null;

  return (
    <section className="vsv-section">
      <header className="vsv-section-head">
        <h2>Pending approvals</h2>
        <span className="vsv-count">{items.length}</span>
      </header>
      {error && <div className="vsv-error">{error}</div>}
      <div className="vsv-members">
        {items.map((r) => {
          const meta = ROLE_META[r.role] || {};
          return (
            <article key={r.id} className="vsv-member">
              <div className="vsv-member-head">
                <span className="vsv-member-name">
                  {r.user?.name || "(unnamed)"}
                </span>
                <span className={`vsv-chip vsv-chip-${meta.chip || "neutral"}`}>
                  {meta.label || r.role || "Member"}
                </span>
              </div>
              <div className="vsv-member-meta">
                {r.relation && <span className="vsv-relation">{r.relation} · </span>}
                <span>Requested {fmtRequestedDate(r.requested_at)}</span>
              </div>
              <div className="vsv-approval-actions">
                <button
                  className="vsa-btn-approve"
                  disabled={busyId === r.id}
                  onClick={() => decide(r, "approve")}
                >
                  {busyId === r.id ? "…" : "Approve"}
                </button>
                <button
                  className="vsa-btn-reject"
                  disabled={busyId === r.id}
                  onClick={() => decide(r, "reject")}
                >
                  Reject
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function ListingBadge({ listings }) {
  if (!listings || listings.length === 0) return null;
  return (
    <div className="vsv-listing-badges">
      {listings.map((l) => (
        <Link
          key={l.id}
          to={`/properties/${l.id}`}
          className={`vsv-listing-pill ${l.listing_type?.toLowerCase()} ${l.status?.toLowerCase()}`}
        >
          <Tag size={11} />
          {l.listing_type === "RENT" ? "For Rent" : "For Sale"}
          <span className="vsv-listing-status">· {l.status}</span>
        </Link>
      ))}
    </div>
  );
}

export default function VillaPage() {
  const { villaNumber } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const d = await apiFetch(
        `/api/villas/by-number/${encodeURIComponent(villaNumber)}`,
        { method: "GET", authRequired: true }
      );
      setData(d);
    } catch (e) {
      setError(e?.message || "Could not load villa");
    } finally {
      setLoading(false);
    }
  }, [villaNumber]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return (
      <div className="vsv-shell">
        <div className="vsv-loading">Loading {villaNumber}…</div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="vsv-shell">
        <div className="vsv-error">{error || "Villa not found"}</div>
        <Link to="/residents" className="vsv-back">
          <ArrowLeft size={14} /> Back to Residents
        </Link>
      </div>
    );
  }

  const { villa, owners, residents, photos, listings, is_owner } = data;
  const activeRentSale = listings.filter(
    (l) => l.status === "ACTIVE" || l.status === "DRAFT" || l.status === "PAUSED"
  );

  const listForRent = () => navigate(`/properties/new?villa_id=${villa.id}&type=RENT`);
  const listForSale = () => navigate(`/properties/new?villa_id=${villa.id}&type=SALE`);

  return (
    <div className="vsv-shell">
      <Link to="/residents" className="vsv-back">
        <ArrowLeft size={14} /> Back to Residents
      </Link>

      <header className="vsv-head">
        <div className="vsv-head-row">
          <HomeIcon size={20} strokeWidth={2.1} />
          <h1>{villa.villa_number}</h1>
          <ListingBadge listings={activeRentSale} />
        </div>

        {is_owner && (
          <div className="vsv-actions">
            <button className="vsv-btn-secondary" onClick={listForRent}>
              Put up for rent
            </button>
            <button className="vsv-btn-secondary" onClick={listForSale}>
              Put up for sale
            </button>
          </div>
        )}
      </header>

      <PendingApprovalsSection
        villaNumber={villa.villa_number}
        onDecided={load}
      />

      <section className="vsv-section">
        <header className="vsv-section-head">
          <h2>Owners</h2>
          <span className="vsv-count">{owners.length}</span>
        </header>
        {owners.length === 0 ? (
          <div className="vsv-empty">No approved owners on file.</div>
        ) : (
          <div className="vsv-members">
            {owners.map((m) => (
              <MemberCard key={m.id} m={m} />
            ))}
          </div>
        )}
      </section>

      <section className="vsv-section">
        <header className="vsv-section-head">
          <h2>Residents</h2>
          <span className="vsv-count">{residents.length}</span>
        </header>
        {residents.length === 0 ? (
          <div className="vsv-empty">
            No tenants or family members linked to this villa.
          </div>
        ) : (
          <div className="vsv-members">
            {residents.map((m) => (
              <MemberCard key={m.id} m={m} />
            ))}
          </div>
        )}
      </section>

      <PhotoGrid
        photos={photos}
        canEdit={is_owner}
        villaNumber={villa.villa_number}
        onChanged={load}
      />
    </div>
  );
}
